import { useMemo, useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { get } from 'lodash';
import { Button, Grid } from '@mui/material';
import { AddRounded } from '@mui/icons-material';
import { Link } from 'react-router';
import isBefore from 'date-fns/isBefore';

import CONFIG from '@/config';
import { RestoreDataGrid } from '@/RestoreComponent/DataGrid';
import { mergeCommonGridColConfig } from '@/RestoreComponent/DataGrid/utils';
import { navigateToPlan } from '@/common/utils/wellnessPlan';
import { getRestHeaders } from '@/common/utils/api';
// import { mockPlans } from './mockPlans';

import './WellnessPlan.scss';
import {
  EditClientProfile,
  profileNeedsMissingInfo
} from '../Info/EditClientProfile';
import { convertToStoreLocalTime } from '@/common/utils/time';

const dateCompare = (a, b) => {
  if (isBefore(new Date(b.date), new Date(a.date))) {
    return -1;
  } else if (isBefore(new Date(a.date), new Date(b.date))) {
    return 1;
  }
  return 0;
};

export const massagePlanResponse = res => {
  let index = 0;
  return res?.data
    .map(p => {
      const questions = get(p, 'questions', []);
      return {
        id: index++,
        date: p.plan_confirmed_datetime,
        needState: get(
          questions.filter(q => q.order == 1),
          '[0].answer',
          ''
        ),
        focusArea: get(
          questions.filter(q => q.order == 2),
          '[0].answer',
          ''
        ),
        was_staff_created: get(p, 'was_staff_created', ''),
        recommend1: get(p, 'plan.services[0].description', ''),
        frequency1: get(p, 'plan.services[0].frequency.times', ''),
        recommend2: get(p, 'plan.services[1].description', ''),
        frequency2: get(p, 'plan.services[1].frequency.times', ''),
        recommend3: get(p, 'plan.services[2].description', ''),
        frequency3: get(p, 'plan.services[2].frequency.times', ''),
        questionnaire_slug: get(p, 'questionnaire_slug', ''),
        session: get(p, 'plan.session', '')
      };
    })
    .sort(dateCompare);
};

const WellnessPlan = props => {
  const { customerAllData } = props;
  const customerId = parseInt(customerAllData.id);

  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  //const [profileUpdated, setProfileUpdated] = useState(false);
  const [userEditOpen, setUserEditOpen] = useState(false);

  const columnConfig = useMemo(
    () => [
      mergeCommonGridColConfig({
        field: 'date',
        headerName: 'Date',
        type: 'date',
        flex: 1,
        sortable: false,
        renderCell: p => (
          <Link
            to="#"
            onClick={() =>
              navigateToPlan(
                customerId,
                p.row.questionnaire_slug,
                p.row.session,
                enqueueSnackbar
              )
            }
            className="no-underline"
          >
            {convertToStoreLocalTime(p?.row?.date, 'MM/DD/YYYY')}
          </Link>
        )
      }),
      mergeCommonGridColConfig({
        field: 'needState',
        headerName: 'Need State',
        type: 'string',
        flex: 1.5,
        sortable: false,
        renderCell: p => (
          <span title={p?.row?.needState} className="ellipsify">
            {p?.row?.needState}
          </span>
        )
      }),
      mergeCommonGridColConfig({
        field: 'focusArea',
        headerName: 'Focus Area',
        type: 'string',
        flex: 1.5,
        sortable: false,
        renderCell: p => (
          <span title={p?.row?.focusArea} className="ellipsify">
            {p?.row?.focusArea}
          </span>
        )
      }),
      mergeCommonGridColConfig({
        field: 'recommend1',
        headerName: 'Recommendation #1',
        type: 'string',
        flex: 2,
        sortable: false,
        renderCell: p => (
          <div className="wrap-cell">
            <div title={p?.row?.recommend1}>{p?.row?.recommend1}</div>
            <div title={p?.row?.frequency1}>{p?.row?.frequency1}</div>
          </div>
        )
      }),
      mergeCommonGridColConfig({
        field: 'recommend2',
        headerName: 'Recommendation #2',
        type: 'string',
        flex: 2,
        sortable: false,
        renderCell: p => (
          <div className="wrap-cell">
            <div title={p?.row?.recommend2}>{p?.row?.recommend2}</div>
            <div title={p?.row?.frequency2}>{p?.row?.frequency2}</div>
          </div>
        )
      }),
      mergeCommonGridColConfig({
        field: 'recommend3',
        headerName: 'Recommendation #3',
        type: 'string',
        flex: 2,
        sortable: false,
        renderCell: p => (
          <div className="wrap-cell">
            <div title={p?.row?.recommend3}>{p?.row?.recommend3}</div>
            <div title={p?.row?.frequency3}>{p?.row?.frequency3}</div>
          </div>
        )
      }),
      mergeCommonGridColConfig({
        field: 'was_staff_created',
        headerName: 'Origin',
        type: 'string',
        flex: 1,
        sortable: false,
        renderCell: p => (
          <span
            title={p?.row?.was_staff_created ? 'POW' : 'Consumer'}
            className="ellipsify"
          >
            {p?.row?.was_staff_created ? 'POW' : 'Consumer'}
          </span>
        )
      })
    ],
    []
  );

  const getPlans = async () => {
    setLoading(true);
    try {
      const res = await axios.get(CONFIG.FORMS_API_URL + '/staff/plans', {
        params: { customerId: customerId },
        headers: getRestHeaders()
      });
      setPlans(massagePlanResponse(res?.data));
      // setPlans(massagePlanResponse(mockPlans));
    } catch (e) {
      enqueueSnackbar('A problem was encountered fetching the client plans.', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPlans();
    // for updating the list when this window is activated
    window.onfocus = getPlans;
    return function cleanup() {
      window.onfocus = undefined;
    };
  }, []);

  const addWellnessPlanIfComplete = async () => {
    // if dob or email is missing we need to gather that first
    if (profileNeedsMissingInfo(customerAllData)) {
      setUserEditOpen(true);
    } else {
      addWellnessPlan();
    }
  };

  const addWellnessPlan = async () => {
    const res = await axios.post(
      CONFIG.FORMS_API_URL + '/staff/customer-link/',
      /* eslint-disable */
      JSON.stringify({
        customer_id: customerId,
        questionnaire_slug: 'wellness',
        action: 'start'
      }),
      /* eslint-disable */
      {
        headers: {
          'Content-Type': 'application/json',
          ...getRestHeaders(undefined, 'Bearer')
        }
      }
    );
    const staffLink = get(res, 'data.data.link');
    if (!staffLink) {
      enqueueSnackbar(
        'A problem was encountered fetching customer link from the booking API.',
        { variant: 'error' }
      );
    } else {
      window.open(staffLink, '_blank');
    }
  };

  const clientEditComplete = async (updated: boolean) => {
    setUserEditOpen(false);
    if (updated) {
      addWellnessPlan();
    }
  };

  return (
    <>
      <Grid container className="wellness-plan">
        <Grid xs={12} item display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            style={{ marginBottom: 16 }}
            color="primary"
            startIcon={<AddRounded />}
            onClick={addWellnessPlanIfComplete}
          >
            Wellness Plan
          </Button>
        </Grid>
        <Grid xs={12} item>
          <RestoreDataGrid
            rowCount={plans.length}
            columnConfig={columnConfig}
            rows={plans}
            loading={loading}
            page={1}
            pageSize={40}
            onPageChangeSize={() => {}}
            onPageChange={() => {}}
            sortModel={[]}
            onSortModelChange={() => {}}
          />
        </Grid>
      </Grid>
      {userEditOpen && (
        <EditClientProfile
          open={userEditOpen}
          handleClose={clientEditComplete}
          customerInfo={customerAllData}
          errorText="There is missing information on client account for Wellness Plan."
          titleText="Client Account Update Required"
        />
      )}
    </>
  );
};

export default WellnessPlan;
